import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UzAlert,
  UzButton,
  UzCard,
  UzRadioGroup,
  UzSpinner,
  UzSwitch,
  type UzRadioOption,
} from '@utazzo/ui';
import { BellRing, Download, Mic, Send, Smartphone, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import { canPromptInstall, onInstallPromptChange, promptInstall } from '../install-prompt';
import { DAY_MS, snoozePushBanner } from '../push-banner';
import {
  getPushConfig,
  getPushPreferences,
  listPushDevices,
  removePushDevice,
  sendTestPush,
  updatePushPreferences,
} from '../push.api';
import {
  currentSubscription,
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  isPushActiveHere,
  onPushActiveChange,
  pushSupport,
  readyRegistration,
  setCachedPushPreferences,
} from '../web-push-client';

import type {
  UpdateWebPushPreferencesRequest,
  WebPushPreferences,
  WebPushPreview,
} from '@utazzo/shared';

import {
  micPermission,
  primeMicrophone,
  requestMicrophone,
  type MicState,
} from '@/features/whatsapp/mic-permission';
import { useCanTakeWhatsAppCalls } from '@/features/whatsapp/use-can-take-calls';
import { extractServerError } from '@/lib/api-error';
import { isAppleMobile, isPhoneDevice, isStandaloneApp } from '@/lib/device';

const PREVIEW_OPTIONS: ReadonlyArray<UzRadioOption<WebPushPreview>> = [
  {
    value: 'FULL',
    label: 'Name and message',
    description: 'Rahul Sharma - "Is the Bali package still available?"',
  },
  {
    value: 'SENDER',
    label: 'Name or title only',
    description: 'Rahul Sharma - "New WhatsApp message"; bell alerts show their title only',
  },
  {
    value: 'HIDDEN',
    label: 'Hide everything',
    description: '"New WhatsApp message" - no name, no text',
  },
];

const PREFS_KEY = ['push', 'preferences'] as const;
const DEVICES_KEY = ['push', 'devices'] as const;

const errorText = (err: unknown, fallback: string): string =>
  extractServerError(err).message ?? fallback;

const permissionNow = (): NotificationPermission | 'unsupported' =>
  typeof window !== 'undefined' && 'Notification' in window
    ? Notification.permission
    : 'unsupported';

/** Device-specific checks, in the order they usually fix things. */
const notArrivingTips = (): string[] => {
  const common = [
    'Tap “Send test” above. If the test arrives, this device is set up - alerts come when something is meant for you: your bell items, chats you can see, and calls on leads assigned to you.',
  ];
  if (isAppleMobile()) {
    return [
      ...common,
      'iPhone needs iOS 16.4 or later (Settings → General → About).',
      'Open Utazzo from its Home Screen icon. Alerts never reach a Safari or Chrome tab. If the icon opens with an address bar, it is only a bookmark: delete it and add it again from Safari, Chrome or Edge: Share → Add to Home Screen.',
      'Settings → Notifications → Utazzo CRM: Allow Notifications, Lock Screen and Banners on.',
      'Turn off Focus / Do Not Disturb, which silences alerts.',
    ];
  }
  if (isPhoneDevice()) {
    return [
      ...common,
      'Settings → Notifications → Chrome (or Utazzo CRM): allow them, with sound, pop-up and lock screen on.',
      'Settings → Apps → Chrome → Battery: “Unrestricted” / “No restrictions”. Xiaomi, Oppo, Vivo and Realme also need “Autostart” on for Chrome.',
      'Turn off Do Not Disturb and data saver.',
    ];
  }
  return [
    ...common,
    'Allow notifications for your browser in the computer’s settings (Windows: Settings → System → Notifications; Mac: System Settings → Notifications).',
    'Turn off Focus / Do Not Disturb.',
    'Keep the browser running in the background - a fully closed browser can’t show alerts.',
  ];
};

const NotArrivingHelp = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: (open: boolean) => void;
}): React.JSX.Element => (
  <details
    open={open}
    onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
    className="group rounded-md border border-zinc-200 px-3 py-2 text-sm"
  >
    <summary className="cursor-pointer font-medium text-zinc-700 select-none">
      Notifications not arriving?
    </summary>
    <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs text-zinc-600">
      {notArrivingTips().map((tip) => (
        <li key={tip}>{tip}</li>
      ))}
    </ol>
  </details>
);

/**
 * "Microphone for calls" - answering a WhatsApp call in Utazzo needs it, and a
 * prompt while the call rings costs the seconds before it stops ringing.
 */
const MicrophoneRow = (): React.JSX.Element | null => {
  const [state, setState] = useState<MicState | null>(null);
  const [asking, setAsking] = useState(false);

  const refresh = useCallback((): void => {
    void micPermission().then(setState);
  }, []);
  useEffect(() => {
    refresh();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const allow = (): void => {
    setAsking(true);
    void requestMicrophone()
      .then((ok) => {
        if (ok) toast.success('Microphone allowed - you can answer calls here.');
        else toast.error('Microphone was not allowed. Allow it in your settings to answer calls.');
      })
      .finally(() => {
        setAsking(false);
        refresh();
      });
  };

  if (state === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
      <Mic className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
      {state === 'granted' ? (
        <span>Microphone allowed for WhatsApp calls.</span>
      ) : state === 'denied' ? (
        <span>
          Microphone is blocked - allow it for Utazzo in your{' '}
          {isAppleMobile() ? 'iPhone Settings' : isPhoneDevice() ? 'phone settings' : 'browser'} to
          answer WhatsApp calls here.
        </span>
      ) : (
        <>
          <span>Allow the microphone now so calls can be answered without delay.</span>
          <UzButton variant="outline" size="sm" onClick={allow} loading={asking}>
            Allow microphone
          </UzButton>
        </>
      )}
    </div>
  );
};

/**
 * "Phone notifications" - lets each user switch push on for the device they
 * are holding and decide what shows up and how much of it each alert
 * reveals. The settings apply to all of the user's devices; the on/off
 * button is per device.
 */
export const PushSettingsCard = (): React.JSX.Element => {
  const support = pushSupport();
  const qc = useQueryClient();

  const config = useQuery({
    queryKey: ['push', 'config'],
    queryFn: getPushConfig,
    enabled: support === 'supported',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const prefs = useQuery({ queryKey: PREFS_KEY, queryFn: getPushPreferences, retry: false });
  const devices = useQuery({ queryKey: DEVICES_KEY, queryFn: listPushDevices, retry: false });

  const activeHere = useSyncExternalStore(onPushActiveChange, isPushActiveHere, () => false);
  const installable = useSyncExternalStore(onInstallPromptChange, canPromptInstall, () => false);
  const [permission, setPermission] = useState(permissionNow);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canTakeCalls = useCanTakeWhatsAppCalls();
  // After a test went out: "did it arrive?" - a No opens the help below.
  const [askArrived, setAskArrived] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Load the worker registration up front: the "Turn on" click must reach
  // `subscribe()` without awaiting anything first (Safari drops the gesture).
  useEffect(() => {
    if (support !== 'supported') return;
    let alive = true;
    void readyRegistration().then((reg) => {
      if (alive) setRegistration(reg);
    });
    return () => {
      alive = false;
    };
  }, [support]);

  const refreshLocal = useCallback(async (): Promise<void> => {
    setPermission(permissionNow());
    const sub = await currentSubscription();
    setThisEndpoint(activeHere && sub ? sub.endpoint : null);
  }, [activeHere]);

  useEffect(() => {
    void refreshLocal();
  }, [refreshLocal]);

  const savePrefs = useMutation({
    mutationFn: (patch: UpdateWebPushPreferencesRequest) => updatePushPreferences(patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: PREFS_KEY });
      const previous = qc.getQueryData<WebPushPreferences>(PREFS_KEY);
      if (previous) qc.setQueryData(PREFS_KEY, { ...previous, ...patch });
      return { previous };
    },
    onError: (err, _patch, ctx) => {
      if (ctx?.previous) qc.setQueryData(PREFS_KEY, ctx.previous);
      toast.error(errorText(err, 'Could not save your notification settings.'));
    },
    onSuccess: (saved) => {
      qc.setQueryData(PREFS_KEY, saved);
      // In-page alerts step aside per category - keep that in step at once.
      setCachedPushPreferences(saved);
    },
  });

  const test = useMutation({
    mutationFn: sendTestPush,
    onSuccess: (r) => {
      if (r.sent > 0) {
        toast.success(
          `Test sent to ${r.sent} device${r.sent === 1 ? '' : 's'}. It should appear in a few seconds.`,
        );
        setAskArrived(true);
      } else {
        toast.error('No device accepted the test. Turn notifications on for this device first.');
      }
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
    },
    onError: (err) => toast.error(errorText(err, 'Could not send a test notification.')),
  });

  const removeDevice = useMutation({
    mutationFn: async (id: string) => {
      const device = devices.data?.find((d) => d.id === id);
      if (device && device.endpoint === thisEndpoint) {
        await disablePushOnThisDevice();
      } else {
        await removePushDevice(id);
      }
    },
    onSuccess: () => {
      toast.success('Device removed.');
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
      void refreshLocal();
    },
    onError: (err) => toast.error(errorText(err, 'Could not remove that device.')),
  });

  const onEnable = (): void => {
    const publicKey = config.data?.publicKey;
    if (!publicKey || !registration) return;
    setBusy(true);
    void enablePushOnThisDevice(publicKey, registration)
      .then((result) => {
        if (result.ok) {
          toast.success('Notifications are on for this device. Sending a test…');
          // Proof it works, straight away (its own toast reports the result).
          test.mutate();
          void qc.invalidateQueries({ queryKey: DEVICES_KEY });
          // Ask for the microphone now, not while a call is ringing.
          if (canTakeCalls) void primeMicrophone();
          return;
        }
        if (result.reason === 'denied') {
          toast.error('Notifications are blocked. Allow them in your settings, then try again.');
        } else {
          toast.error(result.message ?? 'Could not turn on notifications on this device.');
        }
      })
      .finally(() => {
        setBusy(false);
        void refreshLocal();
      });
  };

  const onDisable = (): void => {
    setBusy(true);
    // Switched off on purpose - the installed app's "turn on" banner stays
    // away for a week instead of asking again on the next open.
    snoozePushBanner(7 * DAY_MS);
    void disablePushOnThisDevice()
      .then(() => toast.success('Notifications are off for this device.'))
      .catch((err: unknown) => toast.error(errorText(err, 'Could not turn notifications off.')))
      .finally(() => {
        setBusy(false);
        void qc.invalidateQueries({ queryKey: DEVICES_KEY });
        void refreshLocal();
      });
  };

  const onInstall = (): void => {
    void promptInstall();
  };

  const serverOff = config.isError || (config.data !== undefined && !config.data.enabled);
  const p = prefs.data;
  const androidPhone = isPhoneDevice() && !isAppleMobile();

  return (
    <UzCard
      title="Phone notifications"
      description="Get bell notifications, WhatsApp messages and WhatsApp calls on your phone or computer, even when Utazzo is closed."
    >
      <div className="space-y-5">
        {/* ---- This device ---- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-800">This device</h3>

          {support === 'needs-install' && (
            <UzAlert
              tone="info"
              title="Add Utazzo to your Home Screen first"
              description={
                <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                  <li>In Safari, Chrome or Edge, tap the Share button.</li>
                  <li>Choose “Add to Home Screen”, then Add.</li>
                  <li>Open Utazzo from the Home Screen, sign in and come back to this page.</li>
                </ol>
              }
            />
          )}

          {support === 'unsupported' && (
            <UzAlert
              tone="warning"
              title="This browser can’t receive notifications"
              description="Use Chrome, Safari, Edge or Firefox. On iPhone (iOS 16.4 or later), add Utazzo to the Home Screen from Safari, Chrome or Edge."
            />
          )}

          {support === 'supported' && config.isLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <UzSpinner size="sm" /> Checking…
            </div>
          )}

          {support === 'supported' && serverOff && (
            <UzAlert
              tone="warning"
              title="Not available yet"
              description="Phone notifications aren’t switched on for Utazzo yet. Please check again later."
            />
          )}

          {support === 'supported' && !serverOff && config.data && permission === 'denied' && (
            <UzAlert
              tone="destructive"
              title="Notifications are blocked for Utazzo"
              description={
                isAppleMobile()
                  ? 'Open iPhone Settings → Notifications → Utazzo and allow notifications, then come back.'
                  : 'Tap the lock icon next to the address (or open the app’s site settings), allow Notifications, then reload.'
              }
            />
          )}

          {support === 'supported' && !serverOff && config.data && permission !== 'denied' && (
            <div className="flex flex-wrap items-center gap-2">
              {activeHere ? (
                <>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    <BellRing className="h-3.5 w-3.5" /> On for this device
                  </span>
                  <UzButton
                    variant="outline"
                    size="sm"
                    onClick={() => test.mutate()}
                    loading={test.isPending}
                  >
                    <Send className="mr-1.5 h-4 w-4" />
                    Send test
                  </UzButton>
                  <UzButton variant="ghost" size="sm" onClick={onDisable} disabled={busy}>
                    Turn off here
                  </UzButton>
                </>
              ) : (
                <UzButton onClick={onEnable} loading={busy} disabled={!registration}>
                  <BellRing className="mr-1.5 h-4 w-4" />
                  Turn on for this device
                </UzButton>
              )}
            </div>
          )}

          {askArrived && activeHere && (
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
              <span>Did the test notification arrive?</span>
              <UzButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setAskArrived(false);
                  toast.success('All set - alerts will reach this device.');
                }}
              >
                Yes
              </UzButton>
              <UzButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAskArrived(false);
                  setHelpOpen(true);
                }}
              >
                No
              </UzButton>
            </div>
          )}

          {support === 'supported' && canTakeCalls && <MicrophoneRow />}

          {installable && !isStandaloneApp() && (
            <UzButton variant="outline" size="sm" onClick={onInstall}>
              <Download className="mr-1.5 h-4 w-4" />
              Install Utazzo app
            </UzButton>
          )}

          {androidPhone && activeHere && (
            <p className="text-xs text-zinc-500">
              Alerts arriving late? Set Chrome’s battery usage to “Unrestricted” (Settings → Apps →
              Chrome → Battery). Some phones (Xiaomi, Oppo, Vivo, Realme) also need “Autostart” on
              for Chrome.
            </p>
          )}

          <NotArrivingHelp open={helpOpen} onToggle={setHelpOpen} />
        </section>

        {/* ---- What to send ---- */}
        <section className="space-y-3 border-t border-zinc-100 pt-4">
          <h3 className="text-sm font-semibold text-zinc-800">What to send</h3>
          {prefs.isLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <UzSpinner size="sm" /> Loading…
            </div>
          )}
          {prefs.isError && (
            <p className="text-sm text-zinc-500">
              {errorText(prefs.error, 'Settings are unavailable right now.')}
            </p>
          )}
          {p && (
            <div className="space-y-3">
              <UzSwitch
                label="Bell notifications"
                description="Everything that shows in your bell - lead assignments, approvals, bookings."
                checked={p.notifications}
                onCheckedChange={(v) => savePrefs.mutate({ notifications: v })}
              />
              <UzSwitch
                label="WhatsApp messages"
                description="New customer messages in chats you can see."
                checked={p.whatsappMessages}
                onCheckedChange={(v) => savePrefs.mutate({ whatsappMessages: v })}
              />
              <UzSwitch
                label="WhatsApp calls"
                description="Incoming and missed calls. Tap the alert within a minute to answer in Utazzo."
                checked={p.whatsappCalls}
                onCheckedChange={(v) => savePrefs.mutate({ whatsappCalls: v })}
              />
            </div>
          )}
        </section>

        {/* ---- Preview level ---- */}
        {/* Applies to the whole alert, locked or not - a web page can't tell
            the two apart, only the phone's own lock-screen setting can. */}
        {p && (
          <section className="space-y-3 border-t border-zinc-100 pt-4">
            <UzRadioGroup<WebPushPreview>
              label="What notifications show"
              hint="Applies everywhere, locked or unlocked. To hide details only on the lock screen, choose “Name and message” and turn on “hide content” in your phone’s lock screen notification settings."
              options={PREVIEW_OPTIONS}
              value={p.preview}
              onValueChange={(v) => savePrefs.mutate({ preview: v })}
            />
          </section>
        )}

        {/* ---- Devices ---- */}
        {devices.data && devices.data.length > 0 && (
          <section className="space-y-2 border-t border-zinc-100 pt-4">
            <h3 className="text-sm font-semibold text-zinc-800">Your devices</h3>
            <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
              {devices.data.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-3 py-2">
                  <Smartphone className="h-4 w-4 shrink-0 text-zinc-400" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-zinc-800">
                      {d.deviceLabel ?? 'Device'}
                      {d.endpoint === thisEndpoint && (
                        <span className="ml-2 text-xs font-medium text-emerald-700">
                          This device
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Added {new Date(d.createdAt).toLocaleDateString('en-GB')}
                      {d.lastSuccessAt &&
                        ` · last alert ${new Date(d.lastSuccessAt).toLocaleDateString('en-GB')}`}
                    </div>
                  </div>
                  <UzButton
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${d.deviceLabel ?? 'device'}`}
                    onClick={() => removeDevice.mutate(d.id)}
                    disabled={removeDevice.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </UzButton>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </UzCard>
  );
};
